import { ApiService } from './api.service';
import { TranscriptionEvent } from './AssemblyAIStreamingService';

export interface TranscriptSegment {
  text: string;
  speaker?: string;
  timestamp: number;
  confidence: number;
  roomPid: string;
}

interface TranscriptCollectionConfig {
  meetingId?: string;
  flushInterval?: number;
  maxSegmentsBeforeFlush?: number;
  onFlushSuccess?: (segmentCount: number) => void;
  onFlushError?: (error: Error, segmentCount: number) => void;
}

export class TranscriptCollectionService {
  private segments: TranscriptSegment[] = [];
  private flushInterval: number;
  private maxSegmentsBeforeFlush: number;
  private flushTimer: any = null;
  private meetingId: string | null = null;
  private isActive = false;
  private config: TranscriptCollectionConfig | null = null;

  constructor() {
    this.flushInterval = 30000; // 30 seconds default
    this.maxSegmentsBeforeFlush = 20; // Flush after 20 segments default
  }

  /**
   * Start collecting transcript segments for a meeting
   */
  startCollection(meetingId: string, config?: TranscriptCollectionConfig): void {
    // If already active with a DIFFERENT meeting ID, force stop the old one
    if (this.isActive) {
      if (this.meetingId !== meetingId) {
        console.warn(
          `[TranscriptCollectionService] ⚠️ Stopping stale collection for ${this.meetingId} before starting ${meetingId}`
        );
        // Synchronous stop - clear state immediately
        this._stopCollectionSync();
      } else {
        console.warn(`[TranscriptCollectionService] Collection already active for ${meetingId}`);
        return;
      }
    }

    console.log('[TranscriptCollectionService] Starting collection for', meetingId);

    this.meetingId = meetingId;
    this.config = config || {};
    this.isActive = true;
    this.segments = [];

    // Apply custom config
    if (config?.flushInterval) {
      this.flushInterval = config.flushInterval;
    }
    if (config?.maxSegmentsBeforeFlush) {
      this.maxSegmentsBeforeFlush = config.maxSegmentsBeforeFlush;
    }

    this.scheduleFlush();
    this._log(`Started collection for meeting: ${meetingId}`);
  }

  /**
   * Add a transcription event to the collection buffer
   * Only final transcriptions are collected to avoid duplicates
   */
  addSegment(event: TranscriptionEvent): void {
    if (!this.isActive) {
      console.warn('[TranscriptCollectionService] Cannot add segment - collection not active');
      return;
    }

    // Only collect final transcriptions to avoid duplicates and partial text
    if (!event.isFinal) {
      return;
    }

    const segment: TranscriptSegment = {
      text: event.text,
      speaker: event.speaker,
      timestamp: event.timestamp,
      confidence: event.confidence,
      roomPid: event.roomPid,
    };

    this.segments.push(segment);
    this._log(
      `Added segment (${this.segments.length}/${this.maxSegmentsBeforeFlush}): "${event.text.substring(0, 50)}..."`
    );

    // Flush if buffer gets large enough
    if (this.segments.length >= this.maxSegmentsBeforeFlush) {
      this._log('Max segments reached, flushing...');
      this.flush();
    }
  }

  /**
   * Schedule periodic flushing of transcript segments
   */
  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      if (this.segments.length > 0) {
        this._log(`Periodic flush triggered (${this.segments.length} segments)`);
        this.flush().catch((err) => {
          if (this.config?.onFlushError) {
            this.config.onFlushError(err, this.segments.length);
          } else {
            console.error('[TranscriptCollectionService] Error during periodic flush:', err);
          }
        });
      }
    }, this.flushInterval);
  }

  /**
   * Flush accumulated segments to the backend Redis buffer
   */
  private async flush(): Promise<void> {
    if (this.segments.length === 0) {
      return;
    }

    if (!this.meetingId) {
      console.error('[TranscriptCollectionService] Cannot flush - no meeting ID');
      return;
    }

    const batch = [...this.segments];
    this.segments = []; // Clear buffer immediately

    try {
      this._log(`Flushing ${batch.length} segments to backend Redis...`);

      await ApiService.post(`recordings/${this.meetingId}/transcript-segments`, {
        segments: batch,
      });

      this._log(`✅ Successfully flushed ${batch.length} segments to Redis`);
      this.config?.onFlushSuccess?.(batch.length);
    } catch (error) {
      console.error('[TranscriptCollectionService] ❌ Failed to flush segments:', error);

      // Re-add to buffer on failure (at the beginning to maintain order)
      this.segments.unshift(...batch);
      this._log(`Re-added ${batch.length} segments to buffer after flush failure`);

      this.config?.onFlushError?.(error as Error, batch.length);
    }
  }

  /**
   * Finalize transcript - saves from Redis to S3
   * Should be called when recording stops
   */
  private async finalizeTranscript(): Promise<void> {
    if (!this.meetingId) {
      return;
    }

    try {
      this._log('Finalizing transcript - saving to S3...');

      const response = await ApiService.post(
        `recordings/${this.meetingId}/transcript-finalize`,
        {}
      );

      this._log('✅ Transcript finalized:', response);
    } catch (error) {
      console.error('[TranscriptCollectionService] ❌ Failed to finalize transcript:', error);
    }
  }

  /**
   * Stop collection and perform final flush + finalization
   * @param shouldFinalize - Only the recording initiator should finalize (move from Redis to S3)
   */
  async stopCollection(shouldFinalize: boolean = false): Promise<void> {
    if (!this.isActive) {
      return;
    }

    this._log('Stopping collection...');

    // Clear the interval timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Final flush of any remaining segments to Redis
    if (this.segments.length > 0) {
      this._log(`Final flush of ${this.segments.length} segments to Redis`);
      await this.flush();
    }

    if (shouldFinalize) {
      this._log('This client will finalize the transcript (recording initiator)');
      await this.finalizeTranscript();
    } else {
      this._log('Skipping finalization');
    }

    this.isActive = false;
    this.meetingId = null;
    this.config = null;
    this.segments = [];

    this._log('✅ Collection stopped');
  }

  /**
   * Synchronous stop without async finalization
   * Used internally when forcing cleanup of stale state
   * Note: Any unflushed segments are lost
   */
  private _stopCollectionSync(): void {
    if (!this.isActive) {
      return;
    }

    this._log('Stopping collection (sync - stale state cleanup)...');

    // Clear the interval timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Note: Segments are lost if not already flushed
    // This is acceptable for stale state cleanup
    this.isActive = false;
    this.meetingId = null;
    this.config = null;
    this.segments = [];

    this._log('✅ Collection stopped (sync)');
  }

  /**
   * Check if collection is currently active
   */
  isCollecting(): boolean {
    return this.isActive;
  }

  /**
   * Get current meeting ID
   */
  getCurrentMeetingId(): string | null {
    return this.meetingId;
  }

  /**
   * Get number of segments in buffer
   */
  getBufferSize(): number {
    return this.segments.length;
  }

  /**
   * Manually trigger a flush (useful for testing or forced syncs)
   */
  async manualFlush(): Promise<void> {
    await this.flush();
  }

  private _log(...message: any): void {
    console.log('[TranscriptCollectionService]', ...message);
  }
}

// Singleton instance
export const transcriptCollectionService = new TranscriptCollectionService();
